<template>
  <div class="client-library" :style="cssProps">
    <header class="client-library__header">
      <h1 class="client-library__title">Your resources</h1>
      <p class="client-library__subtitle">
        Documents and links your advisor has shared with you.
      </p>
    </header>

    <main class="client-library__body">
      <!-- Loading -->
      <div v-if="loading" class="client-library__center">
        <v-progress-circular indeterminate size="48" color="primary" />
      </div>

      <!-- Expired / invalid link -->
      <div v-else-if="error === 'expired'" class="client-library__center">
        <v-icon size="48" color="grey">bhi-alert</v-icon>
        <h2 class="client-library__msg-title">This link has expired</h2>
        <p class="client-library__msg">
          Reply to your most recent message to get a fresh link to your resources.
        </p>
      </div>

      <!-- Generic error -->
      <div v-else-if="error" class="client-library__center">
        <v-icon size="48" color="grey">bhi-alert</v-icon>
        <h2 class="client-library__msg-title">Something went wrong</h2>
        <p class="client-library__msg">Please try opening the link again.</p>
      </div>

      <template v-else>
        <!-- Breadcrumb -->
        <nav class="client-library__breadcrumb">
          <button class="client-library__crumb" type="button" @click="goToCrumb(-1)">
            Home
          </button>
          <template v-for="(crumb, index) in breadcrumbs">
            <span :key="'sep-' + index" class="client-library__crumb-sep">/</span>
            <button
              :key="'crumb-' + index"
              class="client-library__crumb"
              type="button"
              @click="goToCrumb(index)"
            >
              {{ crumb.name }}
            </button>
          </template>
        </nav>

        <!-- Empty -->
        <div v-if="!items.length" class="client-library__center">
          <v-icon size="48" color="grey">bhi-folder-filled</v-icon>
          <p class="client-library__msg">No resources here yet.</p>
        </div>

        <!-- Grid of folders + files -->
        <div v-else class="client-library__grid">
          <button
            v-for="item in items"
            :key="item.id"
            type="button"
            class="client-library__tile"
            :class="{ 'is-folder': isFolder(item), 'is-busy': opening === item.id }"
            @click="onTileClick(item)"
          >
            <div class="client-library__thumb">
              <v-progress-circular
                v-if="opening === item.id"
                indeterminate
                size="28"
                color="primary"
              />
              <v-icon v-else-if="isFolder(item)" size="40" color="primary">
                bhi-folder-filled
              </v-icon>
              <img
                v-else-if="item.thumbnailUrl"
                :src="item.thumbnailUrl"
                :alt="displayName(item)"
                class="client-library__thumb-img"
              />
              <span v-else class="client-library__filetype">{{ fileTypeLabel(item) }}</span>
            </div>
            <div class="client-library__meta">
              <span class="client-library__name">{{ displayName(item) }}</span>
              <span v-if="isFolder(item)" class="client-library__count">
                {{ item.contentCount || 0 }} items
              </span>
              <span v-else class="client-library__count">{{ fileTypeLabel(item) }}</span>
            </div>
          </button>
        </div>
      </template>
    </main>

    <v-snackbar v-model="snackbar" :timeout="4000" bottom>
      {{ snackbarText }}
    </v-snackbar>
  </div>
</template>

<script>
import { apiUnloggedInstance } from '@/api/api.js'
import Config from '@/config.json'
import extendedConfig from '@/tenants.js'

// entry_type_id from the backend storage model
const ENTRY_TYPE_FOLDER = 12

// file_type_id -> short label (mirrors the mobile/web library dictionary)
const FILE_TYPE_LABELS = {
  17: 'FILE',
  18: 'IMAGE',
  19: 'VIDEO',
  20: 'PDF',
  21: 'DOC',
  22: 'PPT',
  23: 'XLS',
  52: 'NOTE',
}

export default {
  name: 'ClientMediaLibrary',
  data() {
    return {
      token: null,
      loading: true,
      error: null, // 'expired' | 'generic' | null
      items: [],
      breadcrumbs: [], // [{ id, name }]
      opening: null, // id of the file currently being opened
      snackbar: false,
      snackbarText: '',
    }
  },
  computed: {
    // /prompt/** lives at the backend ROOT, but VUE_APP_BACKEND_URL points at .../api.
    // Strip the /api suffix and call an absolute URL so axios ignores the /api baseURL.
    backendRoot() {
      return (Config.VUE_APP_BACKEND_URL || '').replace(/\/api\/?$/, '')
    },
    cssProps() {
      return {
        '--primary': extendedConfig.VUE_CSS_MAIN_COLOR || '#213665',
      }
    },
  },
  created() {
    this.token = this.$route.query.t || null
    if (!this.token) {
      this.loading = false
      this.error = 'expired'
      return
    }
    this.load(null)
  },
  methods: {
    isFolder(item) {
      return item.entryTypeId === ENTRY_TYPE_FOLDER
    },
    displayName(item) {
      return item.title || item.name || 'Untitled'
    },
    fileTypeLabel(item) {
      return FILE_TYPE_LABELS[item.fileTypeId] || 'FILE'
    },
    async load(dirId) {
      this.loading = true
      this.error = null
      try {
        const params = { t: this.token }
        if (dirId != null) params.dirId = dirId
        const res = await apiUnloggedInstance().get(
          `${this.backendRoot}/prompt/resource/library`,
          { params },
        )
        this.items = Array.isArray(res.data) ? res.data : []
      } catch (e) {
        const status = e && e.response && e.response.status
        this.error = status === 401 || status === 410 ? 'expired' : 'generic'
      } finally {
        this.loading = false
      }
    },
    onTileClick(item) {
      if (this.opening) return
      if (this.isFolder(item)) {
        this.openFolder(item)
      } else {
        this.openFile(item)
      }
    },
    openFolder(item) {
      this.breadcrumbs.push({ id: item.id, name: this.displayName(item) })
      this.load(item.id)
    },
    goToCrumb(index) {
      // index === -1 -> Home (root)
      if (index < 0) {
        this.breadcrumbs = []
        this.load(null)
        return
      }
      this.breadcrumbs = this.breadcrumbs.slice(0, index + 1)
      this.load(this.breadcrumbs[this.breadcrumbs.length - 1].id)
    },
    async openFile(item) {
      this.opening = item.id
      try {
        const res = await apiUnloggedInstance().get(
          `${this.backendRoot}/prompt/resource/file`,
          { params: { t: this.token, resourceId: item.id } },
        )
        const url =
          (res.data && (res.data.previewUrl || res.data.fileUrl)) || null
        if (url) {
          window.open(url, '_blank', 'noopener')
        } else {
          this.notify('This resource is still processing. Try again shortly.')
        }
      } catch (e) {
        const status = e && e.response && e.response.status
        if (status === 401 || status === 410) {
          this.error = 'expired'
        } else {
          this.notify("Couldn't open that resource. Please try again.")
        }
      } finally {
        this.opening = null
      }
    },
    notify(text) {
      this.snackbarText = text
      this.snackbar = true
    },
  },
}
</script>

<style lang="scss" scoped>
.client-library {
  min-height: 100vh;
  background: #f6f6fa;
  padding: 24px 16px 48px;

  &__header {
    max-width: 960px;
    margin: 0 auto 16px;
  }
  &__title {
    font-size: 24px;
    font-weight: 700;
    color: var(--primary);
    margin: 0;
  }
  &__subtitle {
    color: #52575c;
    margin: 4px 0 0;
    font-size: 14px;
  }
  &__body {
    max-width: 960px;
    margin: 0 auto;
  }
  &__center {
    text-align: center;
    padding: 64px 16px;
    color: #52575c;
  }
  &__msg-title {
    font-size: 18px;
    margin: 16px 0 4px;
    color: #25282b;
  }
  &__msg {
    margin: 0;
    font-size: 14px;
  }
  &__breadcrumb {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 16px;
    font-size: 14px;
  }
  &__crumb {
    background: none;
    border: none;
    padding: 2px 4px;
    color: var(--primary);
    cursor: pointer;
    font-weight: 600;
    &:hover {
      text-decoration: underline;
    }
  }
  &__crumb-sep {
    color: #a0a4a8;
  }
  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 16px;
  }
  &__tile {
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    padding: 12px;
    text-align: left;
    cursor: pointer;
    transition: box-shadow 0.15s ease, transform 0.15s ease;
    display: flex;
    flex-direction: column;
    &:hover {
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
      transform: translateY(-1px);
    }
    &.is-busy {
      opacity: 0.6;
      cursor: progress;
    }
  }
  &__thumb {
    height: 96px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f6f8fb;
    border-radius: 6px;
    overflow: hidden;
    margin-bottom: 8px;
  }
  &__thumb-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  &__filetype {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.05em;
    color: #a0a4a8;
  }
  &__meta {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  &__name {
    font-size: 14px;
    font-weight: 600;
    color: #25282b;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &__count {
    font-size: 12px;
    color: #a0a4a8;
  }
}
</style>
